import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CheckCircle2Icon,
  FileTextIcon,
  PencilIcon,
  RefreshCwIcon,
  Trash2Icon,
  UploadCloudIcon,
  XIcon,
  MoveIcon,
} from 'lucide-react';
import {
  CANONICAL_SPECIALISATIONS,
  type DocumentRegistryEntry,
  type IngestJob,
  specialisationLabel,
} from '@kennisbank/shared';
import { ApiError, apiDelete, apiGet, apiPatch, apiPost } from '../api/client.js';
import { Layout } from '../components/Layout.js';
import { SortableTh, toggleSort } from '../components/SortableTh.js';
import { openExternalUrl } from '../lib/url.js';

/**
 * Slice 5 commit 4 — Beheer Kennisbank admin page.
 *
 * Linda's flow:
 *   1. Drop one or more files in the upload zone (or click to pick).
 *   2. Each file gets a progress strip: Uploaden → Valideren → Indexeren → Klaar.
 *   3. On filename collision, a confirm modal asks before overwriting.
 *   4. The document list shows every existing doc with chunkCount + last-ingested.
 *      Per-row: re-ingest, delete (type-to-confirm with chunk count).
 *   5. Single global ingest lock — UI disables the upload zone with a Dutch
 *      "Wachten op huidige indexering…" banner whenever a job is running.
 *
 * RBAC: route is mounted behind RoleGuard for `suppohrt_admin`. Backend
 * re-checks. We render a friendly "Geen toegang" if the user somehow lands
 * here without the role.
 */

const ALLOWED_MIME: Record<string, true> = {
  'application/pdf': true,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': true,
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': true,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': true,
  'application/msword': true,
  'application/vnd.ms-excel': true,
  'application/vnd.oasis.opendocument.text': true,
  'application/vnd.oasis.opendocument.spreadsheet': true,
  'image/jpeg': true,
  'image/png': true,
  'image/webp': true,
  'image/gif': true,
};

const MAX_BYTES = 50 * 1024 * 1024;

/**
 * Per /autoplan auto-decision #11: 13 Dutch error strings keyed on the
 * backend `error` code. Anything not in the map falls back to the
 * server-supplied `message` field.
 */
const DUTCH_ERRORS: Record<string, string> = {
  unsupported_mime:
    'Bestandsformaat niet ondersteund. Gebruik PDF, DOCX, PPTX, ODT of een afbeelding.',
  too_large: 'Bestand te groot. Maximaal 50 MB.',
  too_small: 'Bestand is leeg.',
  md5_mismatch: 'Upload mislukt — controlesom komt niet overeen. Probeer opnieuw.',
  pdf_password_protected:
    'PDF is met wachtwoord beveiligd. Verwijder het wachtwoord en probeer opnieuw.',
  pdf_corrupt: 'PDF lijkt beschadigd of niet leesbaar.',
  content_mismatch:
    'De inhoud van het bestand past niet bij de extensie. Sla het opnieuw op in het juiste formaat.',
  object_missing: 'Geüpload bestand niet gevonden in opslag.',
  filename_collision: 'Bestandsnaam bestaat al. Bevestig overschrijven om door te gaan.',
  ingest_lock_held: 'Een andere indexering loopt nog. Probeer het zo opnieuw.',
  invoke_failed: 'De indexering kon niet worden gestart. Probeer opnieuw.',
  no_extractable_text:
    'Geen tekst gevonden in dit document — waarschijnlijk een afbeelding-PDF (gescand). Gebruik een PDF met selecteerbare tekst, of zet om naar DOCX/PPTX/ODT.',
  extraction_failed:
    'Tekst-extractie mislukt. Controleer of het bestand niet beschadigd is en probeer opnieuw.',
  rate_limited: 'Te veel verzoeken bij de embedding-service. Wacht een minuut en probeer opnieuw.',
  ingest_failed: 'Indexering mislukt. Probeer opnieuw of upload een ander formaat.',
  bad_request: 'Verzoek niet geldig.',
  not_found: 'Document niet gevonden.',
  confirm_mismatch: 'Bestandsnaam komt niet overeen. Typ exact dezelfde naam.',
  forbidden: 'Geen toegang tot Beheer Kennisbank.',
};

function dutchError(err: unknown, fallback = 'Er ging iets mis. Probeer opnieuw.'): string {
  if (err instanceof ApiError) {
    if (DUTCH_ERRORS[err.code]) return DUTCH_ERRORS[err.code];
    // Zod 400s carry a `validation_error` code with a flatten()'d detail
    // whose `fieldErrors` values are the Dutch messages from the backend
    // schema (e.g. "Bestandsnaam ongeldig of niet-ondersteund formaat.").
    // Surface the first one rather than the generic "Request payload
    // failed validation" shell.
    if (err.code === 'validation_error' && err.detail && typeof err.detail === 'object') {
      const fe = (err.detail as { fieldErrors?: Record<string, string[] | undefined> }).fieldErrors;
      const firstField = fe ? Object.values(fe).find((v) => v && v.length > 0) : undefined;
      if (firstField && firstField[0]) return firstField[0];
    }
    return err.message ?? fallback;
  }
  return err instanceof Error ? err.message : fallback;
}

interface UploadUrlResponse {
  jobId: string;
  uploadUrl: string;
  gcsPath: string;
  expiresAt: string;
}

type QueueStage =
  | 'queued'
  | 'uploading'
  | 'validating'
  | 'waiting' // ingest-lock-held; auto-retrying every ~10s
  | 'indexing'
  | 'done'
  | 'error';

interface QueueItem {
  id: string;
  /** Stable display name. Always present, including for items hydrated
   *  from sessionStorage where the underlying File is gone. */
  filename: string;
  /** Live File reference. Undefined for items restored from sessionStorage
   *  after navigation — those can't be retried (no source bytes to PUT
   *  again) but their backend progress is still pollable via jobId. */
  file?: File;
  stage: QueueStage;
  jobId?: string;
  errorMessage?: string;
  /** Backend error.code, set on stage='error'. Drives whether the row
   *  shows the "Alleen als download bewaren" escape hatch. */
  errorCode?: string;
  progressPct?: number;
  /** Target tier captured at enqueue time (the active tab). The upload
   *  request carries this so the agent ingest writes to the correct
   *  collection pair. Stays with the queue item across the polling
   *  cycle so error retries land in the same tier. */
  tier?: Tier;
}

/**
 * Errors where the file uploaded fine but extraction couldn't pull text
 * (image-only PDFs, broken text layer, corrupt PDFs). For these the
 * operator can opt to register the doc as download-only — searchable
 * via /documenten but invisible to the agent. Other failure modes
 * (rate_limited, ingest_failed, invoke_failed) are transient/infra and
 * should NOT offer this escape hatch.
 */
const KEEP_AS_DOWNLOAD_ELIGIBLE_CODES = new Set([
  'no_extractable_text',
  'extraction_failed',
  'pdf_corrupt',
]);

const QUEUE_STORAGE_KEY = 'kennisbank.uploadQueue';

/** Wire shape persisted to sessionStorage. Strips the File. */
interface PersistedQueueItem {
  id: string;
  filename: string;
  stage: QueueStage;
  jobId?: string;
  errorMessage?: string;
  progressPct?: number;
}

interface PendingCollision {
  files: File[];
  resolve: (overwrite: boolean) => void;
}

type Tier = 'ring1' | 'ring0' | 'ring_hq';
interface TabSpec {
  tier: Tier;
  label: string;
  description: string;
}
const TABS: TabSpec[] = [
  {
    tier: 'ring1',
    label: 'Kennisbank',
    description: 'Klinische documenten zichtbaar voor alle gebruikers van de Kennisagent.',
  },
  {
    tier: 'ring0',
    label: 'Intern',
    description: 'Interne SUPPOHRT-documenten — alleen zichtbaar voor SUPPOHRT-medewerkers.',
  },
  {
    tier: 'ring_hq',
    label: 'Kantoor',
    description:
      'Kantoorpersoneel-documenten (processen, beleid) — alleen zichtbaar voor leden van de Kantoorpersoneel-groep.',
  },
];

export function BeheerKennisbankPage(): JSX.Element {
  const [activeTier, setActiveTier] = useState<Tier>('ring1');
  const [documents, setDocuments] = useState<DocumentRegistryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [topError, setTopError] = useState<string | null>(null);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [collision, setCollision] = useState<PendingCollision | null>(null);
  const [pendingDelete, setPendingDelete] = useState<DocumentRegistryEntry | null>(null);
  // Tier-move popover state — when not null, render a small floating menu
  // next to the row asking which tab to move the doc into.
  const [pendingMove, setPendingMove] = useState<DocumentRegistryEntry | null>(null);
  const [reload, setReload] = useState(0);
  const [lockHeld, setLockHeld] = useState(false);
  // Default: newest-first by last ingest. Linda's most common task is
  // verifying / re-ingesting / fixing the doc she just uploaded — bringing
  // it to the top removes a click and a scan every time.
  const [sortBy, setSortBy] = useState<'filename' | 'author' | 'chunkCount' | 'lastIngestedAt'>(
    'lastIngestedAt',
  );
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  // Single-flight upload chain. Every enqueueFiles call (drop, retry,
  // file-picker) appends to this promise so new batches queue behind any
  // in-flight item rather than racing the global ingest lock.
  const queueChainRef = useRef<Promise<void>>(Promise.resolve());
  const dragCounter = useRef(0);
  const [dragOver, setDragOver] = useState(false);
  // Chunks-inspection modal — Linda clicks a doc's chunkCount to spot-check
  // extraction quality. Holds the filename being inspected; null = closed.
  const [inspectChunks, setInspectChunks] = useState<DocumentRegistryEntry | null>(null);

  const refresh = useCallback(() => setReload((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setTopError(null);
    apiGet<{ documents: DocumentRegistryEntry[] }>(
      `/api/v1/beheer/kennisbank/documents?tier=${activeTier}`,
    )
      .then((res) => {
        if (!cancelled) setDocuments(res.documents);
      })
      .catch((err: unknown) => {
        if (!cancelled) setTopError(dutchError(err, 'Kon documentenlijst niet laden.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reload, activeTier]);

  const updateQueueItem = useCallback((id: string, patch: Partial<QueueItem>) => {
    setQueue((items) => items.map((q) => (q.id === id ? { ...q, ...patch } : q)));
  }, []);

  // Persist a slim view of the queue on every change. File objects are
  // stripped (Blobs don't survive JSON.stringify); items WITHOUT a jobId
  // are also stripped because there's no way to recover their backend
  // state without re-dropping the original File. Linda navigating away
  // mid-PUT loses those — acceptable; the indexed jobs survive.
  useEffect(() => {
    const persisted: PersistedQueueItem[] = queue
      .filter((q) => q.jobId !== undefined || q.stage === 'done' || q.stage === 'error')
      .map((q) => ({
        id: q.id,
        filename: q.filename,
        stage: q.stage,
        jobId: q.jobId,
        errorMessage: q.errorMessage,
        progressPct: q.progressPct,
      }));
    if (persisted.length > 0) {
      sessionStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(persisted));
    } else {
      sessionStorage.removeItem(QUEUE_STORAGE_KEY);
    }
  }, [queue]);

  const pollJob = useCallback(
    async (queueId: string, jobId: string): Promise<void> => {
      const start = Date.now();
      // Poll up to 5 min (per-doc target <60s; cold-start can push higher).
      while (Date.now() - start < 5 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 2000));
        let job: IngestJob;
        try {
          job = await apiGet<IngestJob>(`/api/v1/beheer/kennisbank/jobs/${jobId}`);
        } catch (err) {
          updateQueueItem(queueId, { stage: 'error', errorMessage: dutchError(err) });
          return;
        }
        if (job.status === 'succeeded') {
          updateQueueItem(queueId, { stage: 'done' });
          return;
        }
        if (job.status === 'failed' || job.status === 'cancelled') {
          updateQueueItem(queueId, {
            stage: 'error',
            errorCode: job.errorCode,
            errorMessage: DUTCH_ERRORS[job.errorCode ?? ''] ?? 'Indexering mislukt.',
          });
          return;
        }
      }
      updateQueueItem(queueId, {
        stage: 'error',
        errorMessage: 'Indexering duurde te lang. Controleer later het document.',
      });
    },
    [updateQueueItem],
  );

  // On mount: hydrate the queue from sessionStorage. Items with a jobId
  // and a non-terminal stage get a fresh pollJob attached so Linda's
  // wachtrij keeps ticking after she navigates back. Done/error items
  // render as-is so she sees the historical outcome.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    const raw = sessionStorage.getItem(QUEUE_STORAGE_KEY);
    if (!raw) return;
    try {
      const persisted = JSON.parse(raw) as PersistedQueueItem[];
      if (!Array.isArray(persisted) || persisted.length === 0) return;
      const restored: QueueItem[] = persisted.map((p) => ({
        id: p.id,
        filename: p.filename,
        stage: p.stage,
        jobId: p.jobId,
        errorMessage: p.errorMessage,
        progressPct: p.progressPct,
      }));
      setQueue((cur) => [...cur, ...restored]);
      // Re-attach polls for anything still in flight server-side.
      restored.forEach((q) => {
        if (
          q.jobId &&
          (q.stage === 'indexing' || q.stage === 'validating' || q.stage === 'uploading')
        ) {
          void pollJob(q.id, q.jobId);
        }
      });
    } catch {
      sessionStorage.removeItem(QUEUE_STORAGE_KEY);
    }
  }, [pollJob]);

  const processOne = useCallback(
    async (qItem: QueueItem, overwrite: boolean): Promise<void> => {
      const file = qItem.file;
      if (!file) {
        // Restored items from sessionStorage have no File reference; the
        // caller's retry-button gate (canRetry={q.file !== undefined}) is
        // supposed to prevent this branch. Belt-and-braces.
        updateQueueItem(qItem.id, {
          stage: 'error',
          errorMessage: 'Bestand niet meer beschikbaar — sleep het opnieuw in.',
        });
        return;
      }
      try {
        updateQueueItem(qItem.id, { stage: 'uploading', progressPct: 5 });
        const mint = await apiPost<UploadUrlResponse>(
          '/api/v1/beheer/kennisbank/documents/upload-url',
          {
            filename: file.name,
            contentType: file.type || 'application/pdf',
            sizeBytes: file.size,
            overwrite,
            tier: qItem.tier ?? 'ring1',
          },
        );

        // Direct PUT to the signed GCS URL — bypasses the backend's 32MB
        // request-body cap. Content-Type must match the signature.
        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('PUT', mint.uploadUrl);
          xhr.setRequestHeader('Content-Type', file.type || 'application/pdf');
          xhr.upload.onprogress = (ev) => {
            if (ev.lengthComputable) {
              const pct = Math.min(60, 5 + Math.round((ev.loaded / ev.total) * 55));
              updateQueueItem(qItem.id, { progressPct: pct });
            }
          };
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else reject(new Error(`upload HTTP ${xhr.status}`));
          };
          xhr.onerror = () => reject(new Error('netwerkfout tijdens upload'));
          xhr.send(file);
        });

        updateQueueItem(qItem.id, {
          stage: 'validating',
          jobId: mint.jobId,
          progressPct: 65,
        });

        // Finalize with cross-session lock auto-retry. If a previous tab/
        // session left a job running and the global ingest lock is held,
        // sit in 'waiting' state and retry every ~10s rather than dumping
        // a red error on Linda's screen. Total budget ~5 min — past that
        // give up and surface the error so she can investigate.
        const FINALIZE_MAX_ATTEMPTS = 30;
        const FINALIZE_RETRY_MS = 10_000;
        let finalizeAttempt = 0;
        while (true) {
          try {
            await apiPost<{ jobId: string; status: string }>(
              `/api/v1/beheer/kennisbank/documents/${mint.jobId}/finalize`,
              {},
            );
            break;
          } catch (err) {
            const isLockHeld = err instanceof ApiError && err.code === 'ingest_lock_held';
            if (!isLockHeld) throw err;
            finalizeAttempt += 1;
            if (finalizeAttempt >= FINALIZE_MAX_ATTEMPTS) throw err;
            updateQueueItem(qItem.id, {
              stage: 'waiting',
              errorMessage: undefined,
              progressPct: 65,
            });
            setLockHeld(true);
            await new Promise((r) => setTimeout(r, FINALIZE_RETRY_MS));
          }
        }
        setLockHeld(false);

        updateQueueItem(qItem.id, { stage: 'indexing', progressPct: 80 });
        await pollJob(qItem.id, mint.jobId);
      } catch (err) {
        if (err instanceof ApiError && err.code === 'ingest_lock_held') {
          setLockHeld(true);
        }
        updateQueueItem(qItem.id, {
          stage: 'error',
          errorMessage: dutchError(err),
          errorCode: err instanceof ApiError ? err.code : undefined,
        });
      }
    },
    [pollJob, updateQueueItem],
  );

  const enqueueFiles = useCallback(
    async (files: File[]): Promise<void> => {
      const valid: File[] = [];
      const errors: { name: string; reason: string }[] = [];
      for (const f of files) {
        if (!f.type || !ALLOWED_MIME[f.type]) {
          errors.push({ name: f.name, reason: DUTCH_ERRORS.unsupported_mime });
          continue;
        }
        if (f.size > MAX_BYTES) {
          errors.push({ name: f.name, reason: DUTCH_ERRORS.too_large });
          continue;
        }
        if (f.size <= 0) {
          errors.push({ name: f.name, reason: DUTCH_ERRORS.too_small });
          continue;
        }
        valid.push(f);
      }
      if (errors.length > 0) {
        setTopError(errors.map((e) => `${e.name}: ${e.reason}`).join(' · '));
      }
      if (valid.length === 0) return;

      // Filename-collision check (one prompt for the whole batch).
      const existingNames = new Set(documents.map((d) => d.filename.toLowerCase()));
      const colliding = valid.filter((f) => existingNames.has(f.name.toLowerCase()));
      let overwrite = false;
      if (colliding.length > 0) {
        overwrite = await new Promise<boolean>((resolve) => {
          setCollision({ files: colliding, resolve });
        });
        if (!overwrite) {
          // Drop colliding files; keep non-colliding ones.
          const keep = valid.filter((f) => !existingNames.has(f.name.toLowerCase()));
          if (keep.length === 0) return;
          valid.length = 0;
          valid.push(...keep);
        }
      }

      const items: QueueItem[] = valid.map((f) => ({
        id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        filename: f.name,
        file: f,
        stage: 'queued',
        tier: activeTier,
      }));
      setQueue((cur) => [...cur, ...items]);

      // Append to the single-flight chain. Two scenarios:
      //   1. Chain idle → our work runs immediately.
      //   2. Chain busy → our items wait in 'queued' state until the
      //      previous batch's last item finishes its Cloud Run job
      //      (lock released), then we pick up.
      // Catch-then-ignore so one batch's failure doesn't break the chain
      // for the next; per-item errors are already captured in queue state.
      queueChainRef.current = queueChainRef.current
        .catch(() => undefined)
        .then(async () => {
          for (const item of items) {
            await processOne(item, overwrite);
          }
          setLockHeld(false);
          refresh();
        });
    },
    [documents, processOne, refresh, activeTier],
  );

  const onDragEnter = (e: React.DragEvent): void => {
    e.preventDefault();
    dragCounter.current += 1;
    setDragOver(true);
  };
  const onDragLeave = (e: React.DragEvent): void => {
    e.preventDefault();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setDragOver(false);
    }
  };
  const onDrop = (e: React.DragEvent): void => {
    e.preventDefault();
    dragCounter.current = 0;
    setDragOver(false);
    if (lockHeld) return;
    void enqueueFiles(Array.from(e.dataTransfer.files));
  };

  const onFileInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (!e.target.files) return;
    void enqueueFiles(Array.from(e.target.files));
    e.target.value = '';
  };

  /**
   * Slice 6 commit 4 — patch a single metadata field on a doc.
   * Optimistic update: writes the new value into local state immediately,
   * then sends the PATCH. On error, rolls back to the prior value and
   * surfaces the Dutch error string. The 6 editable fields all share this
   * helper since the backend route validates/normalises uniformly.
   */
  const patchMetadata = useCallback(
    async (
      filename: string,
      field:
        | 'author'
        | 'coAuthor'
        | 'documentTitle'
        | 'institution'
        | 'workingGroup'
        | 'documentType',
      newValue: string,
    ): Promise<void> => {
      const trimmed = newValue.trim();
      // Snapshot the current row in case we need to roll back.
      const prior = documents.find((d) => d.filename === filename);
      if (!prior) return;
      const priorValue = (prior as unknown as Record<string, string | undefined>)[field];
      // Optimistic update.
      setDocuments((cur) =>
        cur.map((d) =>
          d.filename === filename
            ? ({ ...d, [field]: trimmed || undefined } as DocumentRegistryEntry)
            : d,
        ),
      );
      try {
        await apiPatch(`/api/v1/beheer/kennisbank/documents/${encodeURIComponent(filename)}`, {
          [field]: trimmed === '' ? null : trimmed,
        });
      } catch (err) {
        // Roll back to prior value.
        setDocuments((cur) =>
          cur.map((d) =>
            d.filename === filename ? ({ ...d, [field]: priorValue } as DocumentRegistryEntry) : d,
          ),
        );
        if (err instanceof ApiError && err.code === 'ingest_lock_held') {
          setLockHeld(true);
        }
        setTopError(dutchError(err, 'Bijwerken mislukt.'));
        throw err;
      }
    },
    [documents],
  );

  const triggerReingest = async (filename: string): Promise<void> => {
    try {
      const res = await apiPost<{ jobId: string; status: string }>(
        `/api/v1/beheer/kennisbank/documents/${encodeURIComponent(filename)}/reingest`,
        {},
      );
      const id = `q_${Date.now()}_re`;
      setQueue((cur) => [
        ...cur,
        {
          id,
          filename,
          file: new File([], filename),
          stage: 'indexing',
          jobId: res.jobId,
          progressPct: 80,
        },
      ]);
      await pollJob(id, res.jobId);
      setLockHeld(false);
      refresh();
    } catch (err) {
      setTopError(dutchError(err));
    }
  };

  return (
    <Layout>
      {/* Mobile gate — the upload + table flows aren't usable below ~768px
          and we don't expect Linda to admin from her phone. Surface a clear
          message instead of a broken layout. */}
      <div className="flex h-full items-center justify-center px-6 md:hidden">
        <div className="max-w-sm text-center">
          <h1 className="text-lg font-semibold text-ink-900">
            Beheer is alleen op desktop beschikbaar
          </h1>
          <p className="mt-2 text-sm text-ink-700">
            Het uploaden en herindexeren van documenten werkt het beste op een laptop of tablet in
            landschapmodus. Open deze pagina opnieuw vanaf een groter scherm.
          </p>
        </div>
      </div>
      <div className="thin-scroll hidden h-full overflow-y-auto md:block">
        <div className="mx-auto max-w-6xl px-6 py-6">
          <header className="mb-6">
            <h1 className="text-2xl font-bold text-ink-950">Beheer Kennisbank</h1>
            <p className="mt-1 text-sm text-ink-700">
              {TABS.find((t) => t.tier === activeTier)?.description ?? ''}
            </p>
          </header>

          {/* Tier tabs (Linda 2026-05-21) — switch the upload target AND
              the doc list filter at the same time. */}
          <div className="mb-4 flex items-end gap-1 border-b border-ring2">
            {TABS.map((t) => {
              const active = activeTier === t.tier;
              return (
                <button
                  key={t.tier}
                  type="button"
                  onClick={() => setActiveTier(t.tier)}
                  className={`relative -mb-px rounded-t-md px-4 py-2 text-sm font-medium transition-colors ${
                    active
                      ? 'border-b-2 border-brand-700 bg-white text-brand-800'
                      : 'text-ink-700/70 hover:bg-brand-50/40 hover:text-ink-900'
                  }`}
                  aria-pressed={active}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          {topError && (
            <div className="mb-4 flex items-start justify-between gap-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              <span>{topError}</span>
              <button
                type="button"
                onClick={() => setTopError(null)}
                className="text-red-700/60 hover:text-red-700"
                aria-label="Foutmelding sluiten"
              >
                <XIcon size={14} />
              </button>
            </div>
          )}

          {lockHeld && (
            <div className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Wachten op huidige indexering… Nieuwe uploads worden geweigerd tot de huidige job
              klaar is.
            </div>
          )}

          <UploadZone
            disabled={lockHeld}
            dragOver={dragOver}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onFileInput={onFileInput}
          />

          {queue.length > 0 && (
            <section className="mt-6 space-y-2">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-700">
                  Wachtrij
                </h2>
                {queue.some((q) => q.stage === 'done' || q.stage === 'error') && (
                  <button
                    type="button"
                    onClick={() =>
                      setQueue((cur) =>
                        cur.filter((q) => q.stage !== 'done' && q.stage !== 'error'),
                      )
                    }
                    className="text-xs text-ink-700/60 hover:text-ink-900"
                  >
                    Wis afgeronde / mislukte
                  </button>
                )}
              </div>
              {queue.map((q) => (
                <QueueRow
                  key={q.id}
                  item={q}
                  // Restored items have no File — retry would re-PUT
                  // nothing. Hide the retry button server-side; let the
                  // user dismiss and re-drop the file from disk.
                  canRetry={q.file !== undefined}
                  onRetry={() => {
                    if (!q.file) return;
                    updateQueueItem(q.id, {
                      stage: 'queued',
                      errorMessage: undefined,
                      errorCode: undefined,
                      progressPct: 0,
                      jobId: undefined,
                    });
                    // Route through the same single-flight chain as fresh
                    // drops. Direct processOne() calls let multiple clicks
                    // race the global ingest lock and dump red errors.
                    const retryItem = { ...q, stage: 'queued' as const, errorMessage: undefined };
                    queueChainRef.current = queueChainRef.current
                      .catch(() => undefined)
                      .then(() => processOne(retryItem, true))
                      .then(() => {
                        setLockHeld(false);
                        refresh();
                      });
                  }}
                  canKeepAsDownload={
                    !!q.errorCode && !!q.jobId && KEEP_AS_DOWNLOAD_ELIGIBLE_CODES.has(q.errorCode)
                  }
                  onKeepAsDownload={async (specialisations) => {
                    if (!q.jobId) return;
                    try {
                      await apiPost<{ jobId: string; status: string; filename: string }>(
                        `/api/v1/beheer/kennisbank/documents/${q.jobId}/keep-as-download`,
                        // Send the operator-picked specialisations alongside
                        // the keep-as-download flip so the practitioner-facing
                        // /documenten card shows the right chip rail without
                        // a second trip to the inline editor.
                        specialisations.length > 0 ? { specialisations } : {},
                      );
                      updateQueueItem(q.id, {
                        stage: 'done',
                        errorMessage: undefined,
                        errorCode: undefined,
                      });
                      refresh();
                    } catch (err) {
                      updateQueueItem(q.id, {
                        stage: 'error',
                        errorMessage: dutchError(err),
                        errorCode: err instanceof ApiError ? err.code : undefined,
                      });
                    }
                  }}
                  onDismiss={() => setQueue((cur) => cur.filter((x) => x.id !== q.id))}
                />
              ))}
            </section>
          )}

          <section className="mt-8">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-700">
                Documenten ({documents.length})
              </h2>
              {loading && <span className="text-xs text-ink-700/60">Laden…</span>}
            </div>
            {/* No overflow-x-auto here — earlier attempt to enable horizontal
                scroll for the action column ended up clipping the filename
                column off the LEFT edge (the table grew wider than the card
                and rendered scrolled past the first column). The fix: let
                long filenames truncate inside their cell with `min-w-0` +
                `truncate` so the row stays inside the card width. The action
                column gets whitespace-nowrap so the trash + Herindexeer
                buttons stay on one line. */}
            <div className="rounded-xl border border-brand-100 bg-white shadow-card">
              <table className="w-full table-fixed text-sm">
                <colgroup>
                  <col />
                  <col className="w-48" />
                  <col className="w-24" />
                  <col className="w-40" />
                  <col className="w-60" />
                </colgroup>
                <thead className="bg-brand-50/50 text-left text-[11px] uppercase tracking-wider text-ink-800/60">
                  <tr>
                    <SortableTh
                      label="Bestand"
                      column="filename"
                      sortBy={sortBy}
                      sortDir={sortDir}
                      onSort={(c) => toggleSort(c, sortBy, setSortBy, sortDir, setSortDir)}
                    />
                    <SortableTh
                      label="Auteur"
                      column="author"
                      sortBy={sortBy}
                      sortDir={sortDir}
                      onSort={(c) => toggleSort(c, sortBy, setSortBy, sortDir, setSortDir)}
                    />
                    <SortableTh
                      label="Chunks"
                      column="chunkCount"
                      sortBy={sortBy}
                      sortDir={sortDir}
                      onSort={(c) => toggleSort(c, sortBy, setSortBy, sortDir, setSortDir)}
                    />
                    <SortableTh
                      label="Laatst geïndexeerd"
                      column="lastIngestedAt"
                      sortBy={sortBy}
                      sortDir={sortDir}
                      onSort={(c) => toggleSort(c, sortBy, setSortBy, sortDir, setSortDir)}
                    />
                    <th className="py-3 pl-4 pr-8"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-50">
                  {sortDocuments(documents, sortBy, sortDir).map((d) => (
                    <tr key={d.filename} className="hover:bg-brand-50/40">
                      <td className="min-w-0 px-4 py-3 font-medium text-ink-900">
                        <button
                          type="button"
                          onClick={() => void openDocument(d.filename, setTopError)}
                          title={`Open ${d.filename} in nieuw tabblad`}
                          className="inline-flex w-full min-w-0 items-center gap-2 text-left text-ink-900 hover:text-brand-700 hover:underline"
                        >
                          <FileTextIcon
                            size={14}
                            className="flex-shrink-0 text-brand-700/60"
                            aria-hidden="true"
                          />
                          <span className="truncate">{d.filename}</span>
                        </button>
                      </td>
                      <td className="px-4 py-3 text-ink-900/70">
                        <EditableCell
                          value={d.author}
                          placeholder="Onbekend"
                          ariaLabel={`Auteur van ${d.filename}`}
                          disabled={lockHeld}
                          required
                          onSave={(v) => patchMetadata(d.filename, 'author', v)}
                        />
                      </td>
                      <td className="px-4 py-3 text-ink-900/70">
                        {d.documentOnly ? (
                          <span
                            title="Alleen beschikbaar als download — de assistent doorzoekt dit document niet."
                            className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800"
                          >
                            Alleen download
                          </span>
                        ) : d.chunkCount > 0 ? (
                          <button
                            type="button"
                            onClick={() => setInspectChunks(d)}
                            title="Bekijk chunks"
                            className="rounded-md px-2 py-0.5 font-medium text-brand-700 hover:bg-brand-50 hover:underline"
                          >
                            {d.chunkCount}
                          </button>
                        ) : (
                          <span>{d.chunkCount}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-ink-900/70">
                        {d.lastIngestedAt
                          ? new Date(d.lastIngestedAt).toLocaleString('nl-NL')
                          : '—'}
                      </td>
                      {/* pr-6 (24px) instead of px-4 on the actions column —
                          the right-aligned trash button looked flush against
                          the card edge with the default 16px padding. */}
                      <td className="whitespace-nowrap py-3 pl-4 pr-4 text-right">
                        <div className="inline-flex gap-2">
                          <button
                            type="button"
                            disabled={lockHeld}
                            onClick={() => void triggerReingest(d.filename)}
                            className="inline-flex items-center gap-1 rounded-md border border-brand-100 px-2 py-1 text-xs text-ink-800 hover:bg-brand-50 disabled:opacity-50"
                          >
                            <RefreshCwIcon size={12} /> Herindexeer
                          </button>
                          <button
                            type="button"
                            disabled={lockHeld}
                            onClick={() => setPendingMove(d)}
                            aria-label={`Verplaats ${d.filename}`}
                            title="Verplaats naar andere tab"
                            className="inline-flex items-center justify-center rounded-md border border-brand-100 p-1.5 text-ink-800 hover:bg-brand-50 disabled:opacity-50"
                          >
                            <MoveIcon size={14} />
                          </button>
                          <button
                            type="button"
                            disabled={lockHeld}
                            onClick={() => setPendingDelete(d)}
                            aria-label={`Verwijder ${d.filename}`}
                            title="Verwijder"
                            className="inline-flex items-center justify-center rounded-md border border-red-100 bg-red-50 p-1.5 text-red-700 hover:bg-red-100 disabled:opacity-50"
                          >
                            <Trash2Icon size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {documents.length === 0 && !loading && (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-ink-700/60">
                        Geen documenten in de kennisbank. Upload het eerste document hierboven.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>

      {collision && (
        <CollisionModal
          files={collision.files}
          onCancel={() => {
            collision.resolve(false);
            setCollision(null);
          }}
          onConfirm={() => {
            collision.resolve(true);
            setCollision(null);
          }}
        />
      )}

      {pendingDelete && (
        <DeleteModal
          doc={pendingDelete}
          onClose={() => setPendingDelete(null)}
          onDeleted={() => {
            setPendingDelete(null);
            refresh();
          }}
          onError={(msg) => setTopError(msg)}
        />
      )}
      {pendingMove && (
        <MoveTierModal
          doc={pendingMove}
          currentTier={activeTier}
          onClose={() => setPendingMove(null)}
          onMoved={() => {
            setPendingMove(null);
            refresh();
          }}
          onError={(msg) => setTopError(msg)}
        />
      )}
      {inspectChunks && <ChunksModal doc={inspectChunks} onClose={() => setInspectChunks(null)} />}
    </Layout>
  );
}

function MoveTierModal({
  doc,
  currentTier,
  onClose,
  onMoved,
  onError,
}: {
  doc: DocumentRegistryEntry;
  currentTier: Tier;
  onClose: () => void;
  onMoved: () => void;
  onError: (msg: string) => void;
}): JSX.Element {
  const [moving, setMoving] = useState<Tier | null>(null);
  const targets = TABS.filter((t) => t.tier !== currentTier);

  async function moveTo(target: Tier): Promise<void> {
    setMoving(target);
    try {
      await apiPatch(
        `/api/v1/beheer/kennisbank/documents/${encodeURIComponent(doc.filename)}/tier`,
        { tier: target },
      );
      onMoved();
    } catch (err: unknown) {
      onError(
        err instanceof ApiError
          ? `Verplaatsen mislukt (${err.status}): ${err.message}`
          : 'Verplaatsen mislukt.',
      );
      setMoving(null);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="move-tier-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="move-tier-title" className="text-lg font-semibold text-ink-900">
          Verplaats document
        </h2>
        <p className="mt-1 text-sm text-ink-700">
          <span className="font-medium">{doc.documentTitle || doc.filename}</span> staat nu in de{' '}
          <span className="font-medium">
            {TABS.find((t) => t.tier === currentTier)?.label ?? currentTier}
          </span>{' '}
          tab. Naar welke tab wil je het verplaatsen?
        </p>
        <div className="mt-4 space-y-2">
          {targets.map((t) => (
            <button
              key={t.tier}
              type="button"
              disabled={moving !== null}
              onClick={() => void moveTo(t.tier)}
              className="flex w-full items-start justify-between gap-3 rounded-lg border border-brand-100 bg-white px-3 py-2 text-left text-sm hover:bg-brand-50 disabled:opacity-50"
            >
              <span>
                <span className="block font-medium text-ink-900">{t.label}</span>
                <span className="block text-xs text-ink-700/70">{t.description}</span>
              </span>
              <span className="shrink-0 text-xs text-brand-700">
                {moving === t.tier ? 'Verplaatsen…' : 'Kies →'}
              </span>
            </button>
          ))}
        </div>
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={moving !== null}
            className="rounded-md px-3 py-1.5 text-sm text-ink-700 hover:bg-brand-50 disabled:opacity-50"
          >
            Annuleren
          </button>
        </div>
      </div>
    </div>
  );
}

interface UploadZoneProps {
  disabled: boolean;
  dragOver: boolean;
  onDragEnter: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onFileInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

function UploadZone({
  disabled,
  dragOver,
  onDragEnter,
  onDragLeave,
  onDrop,
  onFileInput,
}: UploadZoneProps): JSX.Element {
  return (
    <label
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      className={`flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
        disabled
          ? 'cursor-not-allowed border-ink-200 bg-ink-50 opacity-60'
          : dragOver
            ? 'border-brand-500 bg-brand-50'
            : 'border-brand-200 bg-white hover:border-brand-400'
      }`}
    >
      <UploadCloudIcon size={28} className="text-brand-700" />
      <span className="text-sm font-medium text-ink-900">
        Sleep bestanden hierheen of klik om te kiezen
      </span>
      <span className="text-xs text-ink-700/60">
        PDF, DOCX, PPTX, ODT, JPG, PNG, WebP, GIF — maximaal 50 MB per bestand
      </span>
      <input
        type="file"
        multiple
        accept=".pdf,.docx,.doc,.pptx,.odt,.xlsx,.xls,.ods,.jpg,.jpeg,.jfif,.png,.webp,.gif"
        disabled={disabled}
        onChange={onFileInput}
        className="hidden"
      />
    </label>
  );
}

function QueueRow({
  item,
  onRetry,
  onDismiss,
  canRetry,
  canKeepAsDownload,
  onKeepAsDownload,
}: {
  item: QueueItem;
  onRetry: () => void;
  onDismiss: () => void;
  /** Falls back to true for non-restored items. False when the underlying
   *  File reference is gone (queue rehydrated from sessionStorage). */
  canRetry: boolean;
  /** True when the failure is in the eligible-error-codes set AND we have
   *  a jobId to send to /keep-as-download. False otherwise (transient
   *  failures like rate_limited shouldn't tempt the operator into
   *  hiding them as download-only). */
  canKeepAsDownload: boolean;
  /** Operator-picked specialisations applied to the registry entry on
   *  keep-as-download. Empty array = no tags (matches pre-fix behaviour
   *  for users who skip the picker). */
  onKeepAsDownload: (specialisations: string[]) => void | Promise<void>;
}): JSX.Element {
  // Picker state: null = collapsed (button visible), array = expanded form
  // (chip rail + Bewaar/Annuleer). Capped at 3 picks to match the
  // auto-tagger's MAX_TAGS so the chip rail doesn't get overcrowded.
  const [pickedSpecs, setPickedSpecs] = useState<string[] | null>(null);
  const [savingKeep, setSavingKeep] = useState(false);
  const togglePick = (s: string) =>
    setPickedSpecs((cur) => {
      if (!cur) return cur;
      if (cur.includes(s)) return cur.filter((x) => x !== s);
      if (cur.length >= 3) return cur; // cap at 3 — silent ignore once full
      return [...cur, s];
    });
  const stageLabel: Record<QueueStage, string> = {
    queued: 'In wachtrij',
    uploading: 'Uploaden',
    validating: 'Valideren',
    waiting: 'Wachten op andere indexering…',
    indexing: 'Indexeren',
    done: 'Klaar',
    error: 'Fout',
  };
  const isError = item.stage === 'error';
  const isDone = item.stage === 'done';
  const isWaiting = item.stage === 'waiting';
  const isPickerOpen = pickedSpecs !== null;
  return (
    <div
      className={`flex flex-col gap-2 rounded-lg border px-3 py-2 text-sm ${
        isError
          ? 'border-red-100 bg-red-50'
          : isDone
            ? 'border-emerald-100 bg-emerald-50'
            : isWaiting
              ? 'border-amber-100 bg-amber-50'
              : 'border-brand-100 bg-white'
      }`}
    >
      <div className="flex items-center gap-3">
        {isDone ? (
          <CheckCircle2Icon size={16} className="text-emerald-600" />
        ) : isError ? (
          <XIcon size={16} className="text-red-700" />
        ) : isWaiting ? (
          <RefreshCwIcon size={16} className="animate-spin text-amber-700" />
        ) : (
          <FileTextIcon size={16} className="text-brand-700/60" />
        )}
        <div className="flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <span className="font-medium text-ink-900">{item.filename}</span>
            <span className="text-xs text-ink-700/60">{stageLabel[item.stage]}</span>
          </div>
          {!isError && !isDone && (
            <div
              className={`mt-1 h-1 overflow-hidden rounded-full ${isWaiting ? 'bg-amber-100' : 'bg-brand-50'}`}
            >
              <div
                className={`h-full transition-all ${isWaiting ? 'bg-amber-500' : 'bg-brand-600'}`}
                style={{ width: `${item.progressPct ?? 0}%` }}
              />
            </div>
          )}
          {isError && item.errorMessage && (
            <p className="mt-1 text-xs text-red-700">{item.errorMessage}</p>
          )}
        </div>
        {isError && (
          <div className="flex items-center gap-1">
            {canKeepAsDownload && pickedSpecs === null && (
              <button
                type="button"
                onClick={() => setPickedSpecs([])}
                title="Bewaar het bestand alleen als download — de assistent doorzoekt het niet."
                className="inline-flex items-center gap-1 rounded-md border border-brand-200 bg-white px-2 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50"
              >
                <FileTextIcon size={12} /> Alleen download
              </button>
            )}
            {canRetry && (
              <button
                type="button"
                onClick={onRetry}
                title="Probeer opnieuw"
                className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-white px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
              >
                <RefreshCwIcon size={12} /> Probeer opnieuw
              </button>
            )}
            <button
              type="button"
              onClick={onDismiss}
              aria-label="Sluit"
              title="Sluit"
              className="inline-flex items-center justify-center rounded-md p-1 text-red-700/60 hover:bg-red-100 hover:text-red-700"
            >
              <XIcon size={14} />
            </button>
          </div>
        )}
        {isDone && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Sluit"
            title="Sluit"
            className="inline-flex items-center justify-center rounded-md p-1 text-emerald-700/60 hover:bg-emerald-100 hover:text-emerald-700"
          >
            <XIcon size={14} />
          </button>
        )}
      </div>
      {/* Specialisation picker — appears inline when operator clicks
         "Alleen download". Forces the tags BEFORE the keep-as-download POST
         so the practitioner-facing card shows the chip rail immediately,
         without a second trip to /documenten. Picker stays optional —
         "Bewaar zonder tags" submits with an empty array. */}
      {isPickerOpen && (
        <div className="rounded-md border border-amber-200 bg-amber-50/50 p-2">
          <p className="mb-2 text-xs font-medium text-amber-900">
            Welke specialisaties horen bij dit document? (max 3)
          </p>
          <div className="mb-2 flex flex-wrap gap-1">
            {CANONICAL_SPECIALISATIONS.map((s) => {
              const picked = (pickedSpecs ?? []).includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => togglePick(s)}
                  className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] transition-colors ${
                    picked
                      ? 'border-brand-600 bg-brand-600 text-white'
                      : 'border-brand-200 bg-white text-ink-800 hover:bg-brand-50'
                  }`}
                >
                  {specialisationLabel(s)}
                </button>
              );
            })}
          </div>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setPickedSpecs(null)}
              disabled={savingKeep}
              className="rounded-md border border-brand-200 bg-white px-2 py-1 text-xs text-ink-800 hover:bg-brand-50 disabled:opacity-50"
            >
              Annuleer
            </button>
            <button
              type="button"
              onClick={async () => {
                const specs = pickedSpecs ?? [];
                setSavingKeep(true);
                try {
                  await onKeepAsDownload(specs);
                  setPickedSpecs(null);
                } finally {
                  setSavingKeep(false);
                }
              }}
              disabled={savingKeep}
              className="rounded-md border border-brand-600 bg-brand-600 px-2 py-1 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {savingKeep
                ? 'Bezig…'
                : (pickedSpecs?.length ?? 0) === 0
                  ? 'Bewaar zonder tags'
                  : `Bewaar (${pickedSpecs?.length})`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface CollisionModalProps {
  files: File[];
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Slice 6 commit 4 — single-cell inline-edit affordance.
 *
 * Click → input field with current value pre-filled. Enter saves;
 * Escape cancels; blur saves silently if the value changed (matches the
 * spec from the office-hours session). Optimistic update lives on the
 * parent, which writes through to the backend via patchMetadata; this
 * component just handles the local input state + keyboard semantics.
 */
interface EditableCellProps {
  value: string | undefined;
  placeholder: string;
  ariaLabel: string;
  disabled?: boolean;
  required?: boolean;
  onSave: (v: string) => Promise<void>;
}

function EditableCell({
  value,
  placeholder,
  ariaLabel,
  disabled,
  required,
  onSave,
}: EditableCellProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  // Re-sync draft if upstream value changes while we're not editing
  // (e.g. parent's optimistic update completes server confirmation).
  useEffect(() => {
    if (!editing) setDraft(value ?? '');
  }, [value, editing]);

  const startEdit = (): void => {
    if (disabled || saving) return;
    setEditing(true);
  };

  const commit = async (next: string): Promise<void> => {
    const cleaned = next.trim();
    // Required-field rule: don't accept empty submission for required fields.
    // Treat empty as cancel.
    if (required && cleaned === '') {
      setDraft(value ?? '');
      setEditing(false);
      return;
    }
    if (cleaned === (value ?? '').trim()) {
      // No change — quiet exit.
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(cleaned);
      setEditing(false);
    } catch {
      // Parent rolled back + surfaced the error already.
      setDraft(value ?? '');
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void commit(draft);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setDraft(value ?? '');
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={draft}
        disabled={saving}
        aria-label={ariaLabel}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void commit(draft)}
        onKeyDown={onKeyDown}
        className="w-full rounded border border-brand-300 bg-white px-1.5 py-0.5 text-sm focus:border-brand-500 focus:outline-none disabled:bg-brand-50"
      />
    );
  }

  const displayValue = value && value.trim() ? value : placeholder;
  const isPlaceholder = !value || !value.trim();

  return (
    <button
      type="button"
      onClick={startEdit}
      disabled={disabled}
      aria-label={`${ariaLabel} bewerken`}
      className={`group inline-flex items-center gap-1.5 rounded px-1 py-0.5 text-left text-sm transition-colors hover:bg-brand-50/60 disabled:cursor-not-allowed disabled:opacity-50 ${
        isPlaceholder ? 'italic text-ink-700/50' : 'text-ink-900/70'
      }`}
    >
      <span>{displayValue}</span>
      <PencilIcon size={11} className="text-brand-700/40 opacity-0 group-hover:opacity-100" />
    </button>
  );
}

function CollisionModal({ files, onCancel, onConfirm }: CollisionModalProps): JSX.Element {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <h3 className="mb-2 text-lg font-bold text-ink-950">Bestand bestaat al</h3>
        <p className="mb-3 text-sm text-ink-700">
          De volgende bestanden bestaan al in de kennisbank. Kies <strong>Overschrijven</strong> om
          ze te vervangen, of <strong>Sla over</strong> om alleen deze dubbele over te slaan en met
          de overige door te gaan.
        </p>
        <ul className="mb-4 max-h-40 overflow-y-auto rounded-md bg-ink-50 p-2 text-xs text-ink-800">
          {files.map((f) => (
            <li key={f.name} className="py-0.5">
              {f.name}
            </li>
          ))}
        </ul>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-brand-100 px-3 py-1.5 text-sm text-ink-800 hover:bg-brand-50"
          >
            Sla over
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
          >
            Overschrijven
          </button>
        </div>
      </div>
    </div>
  );
}

interface DeleteModalProps {
  doc: DocumentRegistryEntry;
  onClose: () => void;
  onDeleted: () => void;
  onError: (msg: string) => void;
}

function DeleteModal({ doc, onClose, onDeleted, onError }: DeleteModalProps): JSX.Element {
  const [typed, setTyped] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const matches = typed === doc.filename;

  const submit = async (): Promise<void> => {
    if (!matches) return;
    setSubmitting(true);
    try {
      await apiDelete(`/api/v1/beheer/kennisbank/documents/${encodeURIComponent(doc.filename)}`, {
        confirmFilename: doc.filename,
      });
      onDeleted();
    } catch (err) {
      onError(dutchError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <h3 className="mb-2 text-lg font-bold text-red-700">Document verwijderen</h3>
        <p className="mb-2 text-sm text-ink-700">
          Dit verwijdert <strong>{doc.chunkCount}</strong>{' '}
          {doc.chunkCount === 1 ? 'chunk' : 'chunks'} uit de Kennisagent. Verwijdering is permanent
          — een nieuwe upload van hetzelfde bestand zou opnieuw geïndexeerd moeten worden.
        </p>
        <p className="mb-2 text-sm text-ink-700">
          Typ ter bevestiging de bestandsnaam exact:{' '}
          <code className="rounded bg-ink-100 px-1 py-0.5 text-xs">{doc.filename}</code>
        </p>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={doc.filename}
          className="mb-4 w-full rounded-md border border-brand-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
        />
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-brand-100 px-3 py-1.5 text-sm text-ink-800 hover:bg-brand-50"
          >
            Annuleer
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!matches || submitting}
            className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:bg-red-300"
          >
            {submitting ? 'Verwijderen…' : 'Verwijder'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Sort helper -----------------------------------------------------

type SortColumn = 'filename' | 'author' | 'chunkCount' | 'lastIngestedAt';

function sortDocuments(
  docs: DocumentRegistryEntry[],
  by: SortColumn,
  dir: 'asc' | 'desc',
): DocumentRegistryEntry[] {
  const sign = dir === 'asc' ? 1 : -1;
  const out = [...docs];
  out.sort((a, b) => {
    let cmp = 0;
    switch (by) {
      case 'filename':
        cmp = a.filename.toLowerCase().localeCompare(b.filename.toLowerCase());
        break;
      case 'author':
        cmp = a.author.toLowerCase().localeCompare(b.author.toLowerCase());
        break;
      case 'chunkCount':
        cmp = a.chunkCount - b.chunkCount;
        break;
      case 'lastIngestedAt':
        cmp = (a.lastIngestedAt ?? '').localeCompare(b.lastIngestedAt ?? '');
        break;
    }
    return cmp * sign;
  });
  return out;
}

async function openDocument(
  filename: string,
  setTopError: (s: string | null) => void,
): Promise<void> {
  try {
    const res = await apiGet<{ url: string; expiresAt: string }>(
      `/api/v1/beheer/kennisbank/documents/${encodeURIComponent(filename)}/download-url`,
    );
    if (!openExternalUrl(res.url)) {
      setTopError('Kon bronbestand niet openen: ongeldige download-URL.');
    }
  } catch (err) {
    if (err instanceof ApiError && err.code === 'source_not_in_bucket') {
      setTopError(
        `Bronbestand "${filename}" staat niet meer in de bucket. Dit komt voor bij oudere documenten die voor slice 5 zijn geïngest. De inhoud is wel doorzoekbaar, maar het originele bestand is niet langer beschikbaar.`,
      );
      return;
    }
    setTopError(dutchError(err, 'Kon bronbestand niet openen.'));
  }
}

// ---------- ChunksModal ----------------------------------------------------
// Read-only spot-check view for the per-doc chunk extraction. Linda clicks
// the chunkCount in the row → fetches /:filename/chunks → renders each
// chunk's text body with chunk index + page. No editing — chunks regenerate
// on every reingest, so any corrections happen via Bewerk metadata or
// re-uploading the source file.

interface ChunkRow {
  chunkId: string;
  chunkIndex: number;
  page: number | null;
  chunkText: string;
  sectionType: string | null;
  documentSection: string | null;
}

function ChunksModal({
  doc,
  onClose,
}: {
  doc: DocumentRegistryEntry;
  onClose: () => void;
}): JSX.Element {
  const [chunks, setChunks] = useState<ChunkRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiGet<{ filename: string; chunks: ChunkRow[] }>(
      `/api/v1/beheer/kennisbank/documents/${encodeURIComponent(doc.filename)}/chunks`,
    )
      .then((res) => {
        if (!cancelled) setChunks(res.chunks);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Kon chunks niet laden.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [doc.filename]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="chunks-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/50 p-4"
    >
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-brand-100 px-6 py-4">
          <div>
            <h2 id="chunks-title" className="text-lg font-semibold text-ink-900">
              Chunks van {doc.documentTitle || doc.filename}
            </h2>
            <p className="mt-1 text-xs text-ink-900/60">
              {doc.chunkCount} chunks geïndexeerd in <code>kennisbank_{doc.ring ?? 'ring1'}</code>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Sluiten"
            className="rounded-md p-1 text-ink-900/60 hover:bg-brand-50 hover:text-ink-900"
          >
            <XIcon size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {!chunks && !error && (
            <p className="py-10 text-center text-sm text-ink-900/60">Chunks laden…</p>
          )}
          {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
          {chunks && chunks.length === 0 && !error && (
            <p className="py-10 text-center text-sm text-ink-900/60">
              Geen chunks gevonden voor dit document.
            </p>
          )}
          {chunks && chunks.length > 0 && (
            <ol className="space-y-3">
              {chunks.map((c) => (
                <li
                  key={c.chunkId}
                  className="rounded-lg border border-brand-100 bg-brand-50/30 px-4 py-3"
                >
                  <div className="mb-1 flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-ink-800/60">
                    <span>chunk {c.chunkIndex + 1}</span>
                    {c.page !== null && <span>· pagina {c.page}</span>}
                    {c.sectionType && <span>· {c.sectionType}</span>}
                    {c.documentSection && <span>· {c.documentSection}</span>}
                  </div>
                  <p className="whitespace-pre-wrap text-sm text-ink-900">{c.chunkText}</p>
                </li>
              ))}
            </ol>
          )}
        </div>
        <div className="border-t border-brand-100 bg-brand-50/30 px-6 py-3 text-right">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-brand-200 px-4 py-1.5 text-sm text-ink-900 transition-colors hover:bg-brand-50"
          >
            Sluiten
          </button>
        </div>
      </div>
    </div>
  );
}
